/**
 * NASDAQ Quantum — constantes y resolución de config.
 *
 * SAFE_DEFAULTS es la única fuente de verdad de defaults; todo consumo de
 * config pasa por resolveConfig() (patrón E.2 del Standard) para que ninguna
 * clave llegue undefined aunque la config persistida sea parcial o corrupta.
 *
 * Decisión de producto (WP §1/§5, cerrada): las SESIONES (sessA/sessB/sessC
 * + tz) son el ÚNICO grupo de parámetros del motor editable por el usuario
 * -- igual que en el Pine origen, donde son los únicos `input.*` visibles.
 * El resto de parámetros del motor (emaLen, hugLen, hugMin, maxExt, accLen,
 * maxCross, erLen, erMin, rr, maxTrades) son constantes internas fijas, NO
 * se exponen en el panel.
 */
import { DEFAULT_F, DEFAULT_M, resolveClassParams } from '@/utils/signalClass'

export const QUANTUM_COLORS = Object.freeze({
  bull: '#1B5E20',
  bear: '#B71C1C',
  accent: '#B08D57',
  neutral: '#374151',
  range: '#B08D57',
})

// Copy fijo del panel (WP §5, textual, no se parafrasea).
export const PANEL_DISCLAIMER = 'Señales para seguimiento voluntario · sin backtest validado · optimizado para NASDAQ 5m'

export const SAFE_DEFAULTS = Object.freeze({
  // Clase de señal A/A+ (WP A/A+ fase 2): m y f % de la fórmula de la
  // tarjeta (utils/signalClass.js, paridad con backend signal_class.py).
  signalClassM: DEFAULT_M,
  signalClassFeePct: DEFAULT_F,
  _version: 1,

  // ── Activación (mismo patrón que patron_ruptura_quantum: sin toggle en
  // el panel, la activa el menú de indicadores) ───────────────────────────
  enabled: false,

  // ── Visualización ────────────────────────────────────────────────────
  showRangeBox: true,
  showRangeFill: true,
  showEmaLine: true,
  showSignalLabels: true,
  showLevels: true,
  showLastSignals: true,

  // ── Filtro opcional de activos favoritos: [] = TODOS los símbolos
  // (indicador universal, WP §1 -- esto es solo un filtro de conveniencia,
  // no una restricción de símbolo). ───────────────────────────────────────
  assets: [],

  // ── Notificaciones (persistencia de preferencia; el envío real requiere
  // conectar la matriz de señales del backend -- ver ficha §6) ────────────
  notificationsEnabled: false,
  notifyOnSignal: true,
  notifyOnTP: true,
  notifyOnSL: true,

  // ── Sesiones (ÚNICO grupo de parámetros del motor editable) ─────────────
  sessA: '0500-0700',
  sessB: '0700-1030',
  sessC: '0700-1030',
  tz: 'Europe/Madrid',

  // ── Estilo ────────────────────────────────────────────────────────────
  lineWidth: 1.5,

  // ── Colores (null = paleta Quantum) ──────────────────────────────────
  rangeColor: null,
  emaColor: null,
  longColor: null,
  shortColor: null,
  entryColor: null,
  slColor: null,
  tpColor: null,

  // ── Dashboard / panel ────────────────────────────────────────────────
  // {x:8,y:8} lo comparten 12 indicadores del registro; {x:8,y:360} es
  // rango_quantum. Se elige un tercer valor para no apilarse con ninguno.
  badgePosition: { x: 8, y: 180 },
  panelSections: {},
})

export const LINE_WIDTHS = Object.freeze([1, 1.5, 2])

// Símbolos base soportados por la plataforma (mismo catálogo que PRQ).
export const AVAILABLE_SYMBOLS = Object.freeze(['BTC', 'ETH', 'SOL', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK'])

// Zonas horarias ofrecidas en el panel -- Europe/Madrid es la única prevista
// por el WP; se deja la lista abierta a futuras sesiones de otros mercados
// sin tocar el motor (acepta cualquier IANA tz válida).
export const AVAILABLE_TIMEZONES = Object.freeze(['Europe/Madrid'])

export const ZONE_OPACITY = 0.07
export const LABEL_FONT = "'Playfair Display', Georgia, serif"

const SESSION_RE = /^([01]\d|2[0-3])[0-5]\d-([01]\d|2[0-3])[0-5]\d$/

// ── Helpers internos de saneado ─────────────────────────────────────────

function asBool(v, fallback) {
  return v == null ? fallback : Boolean(v)
}

function asColorOrNull(v) {
  return typeof v === 'string' && v.length > 0 ? v : null
}

function asAssets(v) {
  if (!Array.isArray(v)) return [...SAFE_DEFAULTS.assets]
  return v.filter(x => typeof x === 'string' && AVAILABLE_SYMBOLS.includes(x))
}

/** "HHMM-HHMM" válido -- si no, cae al default de esa sesión. */
function asSession(v, fallback) {
  return typeof v === 'string' && SESSION_RE.test(v) ? v : fallback
}

function asTz(v) {
  return typeof v === 'string' && v.length > 0 ? v : SAFE_DEFAULTS.tz
}

/**
 * Normaliza un símbolo de chart a su base en mayúsculas, igual que PRQ.
 */
export function normalizeSymbol(rawSymbol) {
  if (typeof rawSymbol !== 'string' || rawSymbol.length === 0) return null
  let s = rawSymbol.toUpperCase().trim()
  for (const suffix of ['/USDT:USDT', '/USDT', 'USDT']) {
    if (s.endsWith(suffix)) {
      s = s.slice(0, -suffix.length)
      break
    }
  }
  return s || null
}

/** [] = todos los símbolos (indicador universal, filtro solo opcional). */
export function symbolMatches(assets, rawSymbol) {
  if (!Array.isArray(assets)) return false
  if (assets.length === 0) return true
  const base = normalizeSymbol(rawSymbol)
  if (!base) return false
  return assets.some(a => normalizeSymbol(a) === base)
}

/**
 * Config de fábrica para "Restablecer valores Quantum": NUNCA toca `enabled`,
 * `badgePosition` ni `panelSections` (mismo patrón E.2 que PRQ).
 */
export function resetToDefaults(current) {
  const pos = current?.badgePosition
  return {
    ...SAFE_DEFAULTS,
    enabled: current?.enabled ?? SAFE_DEFAULTS.enabled,
    badgePosition: pos && Number.isFinite(pos.x) ? pos : { ...SAFE_DEFAULTS.badgePosition },
    panelSections:
      current?.panelSections && typeof current.panelSections === 'object'
        ? current.panelSections
        : { ...SAFE_DEFAULTS.panelSections },
  }
}

/**
 * Merge defensivo: defaults + config persistida + saneado por clave. Nunca
 * devuelve undefined en ninguna clave conocida.
 */
export function resolveConfig(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...SAFE_DEFAULTS }
  }
  const merged = { ...SAFE_DEFAULTS, ...raw }
  return {
    ...merged,
    // Clase A/A+: número finito >= 0 o default (nunca NaN en la comparación).
    signalClassM: resolveClassParams(merged).m,
    signalClassFeePct: resolveClassParams(merged).f,
    enabled: asBool(merged.enabled, false),
    showRangeBox: asBool(merged.showRangeBox, true),
    showRangeFill: asBool(merged.showRangeFill, true),
    showEmaLine: asBool(merged.showEmaLine, true),
    showSignalLabels: asBool(merged.showSignalLabels, true),
    showLevels: asBool(merged.showLevels, true),
    showLastSignals: asBool(merged.showLastSignals, true),
    assets: asAssets(merged.assets),
    notificationsEnabled: asBool(merged.notificationsEnabled, false),
    notifyOnSignal: asBool(merged.notifyOnSignal, true),
    notifyOnTP: asBool(merged.notifyOnTP, true),
    notifyOnSL: asBool(merged.notifyOnSL, true),
    sessA: asSession(merged.sessA, SAFE_DEFAULTS.sessA),
    sessB: asSession(merged.sessB, SAFE_DEFAULTS.sessB),
    sessC: asSession(merged.sessC, SAFE_DEFAULTS.sessC),
    tz: asTz(merged.tz),
    lineWidth: LINE_WIDTHS.includes(merged.lineWidth) ? merged.lineWidth : SAFE_DEFAULTS.lineWidth,
    rangeColor: asColorOrNull(merged.rangeColor),
    emaColor: asColorOrNull(merged.emaColor),
    longColor: asColorOrNull(merged.longColor),
    shortColor: asColorOrNull(merged.shortColor),
    entryColor: asColorOrNull(merged.entryColor),
    slColor: asColorOrNull(merged.slColor),
    tpColor: asColorOrNull(merged.tpColor),
    badgePosition: merged.badgePosition && Number.isFinite(merged.badgePosition.x)
      ? merged.badgePosition
      : { ...SAFE_DEFAULTS.badgePosition },
    panelSections: merged.panelSections && typeof merged.panelSections === 'object' ? merged.panelSections : {},
  }
}

/** null → color de paleta; string no vacío → tal cual. */
export function resolveColor(value, fallback) {
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

/** Materializa los colores del indicador a partir de una config resuelta. */
export function resolveColors(cfg) {
  return {
    range: resolveColor(cfg.rangeColor, QUANTUM_COLORS.range),
    ema: resolveColor(cfg.emaColor, QUANTUM_COLORS.accent),
    long: resolveColor(cfg.longColor, QUANTUM_COLORS.bull),
    short: resolveColor(cfg.shortColor, QUANTUM_COLORS.bear),
    entry: resolveColor(cfg.entryColor, QUANTUM_COLORS.neutral),
    sl: resolveColor(cfg.slColor, QUANTUM_COLORS.bear),
    tp: resolveColor(cfg.tpColor, QUANTUM_COLORS.bull),
  }
}

/** hex '#RRGGBB' o 'rgb(...)' → 'rgba(r, g, b, alpha)'. */
export function withAlpha(color, alpha) {
  const a = Math.min(1, Math.max(0, alpha))
  if (typeof color !== 'string' || color.length === 0) return `rgba(0,0,0,${a})`
  if (color.startsWith('rgba(')) {
    return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/, `rgba($1, $2, $3, ${a})`)
  }
  if (color.startsWith('rgb(')) {
    const rgb = color.match(/\d+/g)
    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`
  }
  if (color.startsWith('#')) {
    const hex = color.slice(1)
    const n = parseInt(hex, 16)
    if (Number.isNaN(n) || hex.length < 6) return color
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
  }
  return color
}
